import type { MapComponentFilterField } from '../map-style-workbench/types';

import { normalizeAdvancedRuleColor } from './colors';
import {
  normalizeMapLibreLayoutOverrides,
  normalizeMapLibrePaintOverrides,
} from '../map-workbench/styleValueNormalization';
import type { LayerRuleBuilderState, LayerRuleLayerState, LayerRuleOpacityKind } from './types';

type Expr = any;

type EnabledRule = LayerRuleLayerState['rules'][number];

type ZoomInterpolateExpression = {
  kind: 'interpolate';
  operator: 'interpolate' | 'interpolate-hcl' | 'interpolate-lab';
  interpolation: unknown;
  stops: Array<{ input: number; output: unknown }>;
};

type ZoomStepExpression = {
  kind: 'step';
  defaultOutput: unknown;
  stops: Array<{ input: number; output: unknown }>;
};

type ZoomCurveExpression = ZoomInterpolateExpression | ZoomStepExpression;

type PaintBaseline = {
  defined: Record<string, boolean>;
  values: Record<string, unknown>;
};

type LayoutBaseline = {
  defined: Record<string, boolean>;
  values: Record<string, unknown>;
};

type WorkbenchBaseline = {
  filter: unknown | null;
  paint: PaintBaseline;
  layout: LayoutBaseline;
};

type WorkbenchMeta = {
  managed?: boolean;
  source_id?: string;
  source_alias?: string;
  source_layer?: string;
  kind?: string;
  baseline?: WorkbenchBaseline;
};

const MAP_SOURCE_GEOJSON_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson$/;
const RULES_HIDE_ALL_FILTER: Expr = ['all', ['in', ['id'], ['literal', []]]];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasOwn = (value: Record<string, unknown> | null | undefined, key: string) =>
  Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);

const MAPLIBRE_EXPRESSION_OPERATORS = new Set<string>([
  'array',
  'at',
  'all',
  'any',
  'boolean',
  'case',
  'coalesce',
  'collator',
  'concat',
  'format',
  'get',
  'has',
  'id',
  'image',
  'in',
  'interpolate',
  'interpolate-hcl',
  'interpolate-lab',
  'length',
  'let',
  'literal',
  'match',
  'number',
  'object',
  'slice',
  'step',
  'string',
  'to-boolean',
  'to-color',
  'to-number',
  'to-string',
  'typeof',
  'var',
  'within',
  'zoom',
  '!',
  '!=',
  '<',
  '<=',
  '==',
  '>',
  '>=',
]);

type LayerRuleStyleKind = LayerRuleOpacityKind | 'fill-extrusion';

const normalizeOperator = (value: string) => value.trim().toLowerCase();

const parseBoolean = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return null;
};

const coerceLiteral = (raw: string, fieldType: MapComponentFilterField['value_type']): unknown => {
  if (fieldType === 'number') {
    const num = Number(raw);
    return Number.isFinite(num) ? num : raw;
  }
  if (fieldType === 'boolean') {
    const bool = parseBoolean(raw);
    return bool ?? raw;
  }
  return raw;
};

const buildCondition = (
  fieldId: string,
  operatorId: string,
  values: string[],
  field?: MapComponentFilterField,
): Expr | null => {
  const op = normalizeOperator(operatorId);
  if (op === 'match_everything') {
    return ['all'];
  }
  if (!fieldId) return null;
  const accessor: Expr = ['get', fieldId];
  const valueType = field?.value_type ?? 'string';

  const firstRaw = values[0] ?? '';
  const first = coerceLiteral(firstRaw, valueType);

  switch (op) {
    case 'eq':
    case 'equals':
      return ['==', accessor, first];
    case 'neq':
    case 'not_equals':
      return ['!=', accessor, first];
    case 'gt':
    case 'greaterthan':
      return ['>', accessor, coerceLiteral(firstRaw, 'number')];
    case 'gte':
    case 'greaterthanorequal':
      return ['>=', accessor, coerceLiteral(firstRaw, 'number')];
    case 'lt':
    case 'lessthan':
      return ['<', accessor, coerceLiteral(firstRaw, 'number')];
    case 'lte':
    case 'lessthanorequal':
      return ['<=', accessor, coerceLiteral(firstRaw, 'number')];
    case 'contains':
      return ['in', String(firstRaw ?? ''), ['to-string', accessor]];
    case 'in': {
      const literals = (values ?? []).map(value => coerceLiteral(value, valueType));
      return ['match', accessor, ['literal', literals], true, false];
    }
    case 'exists':
      return ['has', fieldId];
    default:
      return ['==', accessor, first];
  }
};

const buildPredicate = (logic: any, fieldsById: Map<string, MapComponentFilterField>): Expr | null => {
  if (!logic || typeof logic !== 'object') return null;

  if (logic.type === 'condition') {
    const fieldId = String(logic.fieldId ?? '');
    const operatorId = String(logic.operatorId ?? 'eq');
    const values = Array.isArray(logic.values) ? logic.values.map(String) : [];
    return buildCondition(fieldId, operatorId, values, fieldsById.get(fieldId));
  }

  if (logic.type === 'group') {
    const operator = logic.operator === 'OR' ? 'OR' : 'AND';
    const children = Array.isArray(logic.children) ? logic.children : [];
    const compiled = children.map((child: any) => buildPredicate(child, fieldsById)).filter(Boolean);
    if (compiled.length === 0) return null;
    if (compiled.length === 1) return compiled[0];
    return operator === 'OR' ? ['any', ...compiled] : ['all', ...compiled];
  }

  return null;
};

const ruleUnionPredicate = (layer: LayerRuleLayerState): Expr | null => {
  const enabled = layer.rules.filter(rule => rule.enabled);
  if (enabled.length === 0) return null;
  const fieldsById = new Map<string, MapComponentFilterField>();
  layer.fields.forEach(field => fieldsById.set(field.id, field));

  const predicates = enabled
    .map(rule => buildPredicate(rule.logic as any, fieldsById))
    .filter(Boolean);
  if (predicates.length === 0) return null;
  return predicates.length === 1 ? predicates[0] : ['any', ...predicates];
};

const propNamesForKind = (kind: LayerRuleStyleKind) => {
  switch (kind) {
    case 'fill':
      return { color: ['fill-color'], opacity: ['fill-opacity'] };
    case 'line':
      return { color: ['line-color'], opacity: ['line-opacity'] };
    case 'circle':
      return { color: ['circle-color'], opacity: ['circle-opacity'] };
    case 'fill-extrusion':
      return { color: ['fill-extrusion-color'], opacity: ['fill-extrusion-opacity'] };
    case 'symbol':
      return { color: ['text-color', 'icon-color'], opacity: ['text-opacity', 'icon-opacity'] };
  }
};

const allowedCustomPaintPropsByKind: Record<LayerRuleStyleKind, Set<string>> = {
  fill: new Set(['fill-color', 'fill-opacity', 'fill-outline-color', 'fill-pattern']),
  line: new Set(['line-blur', 'line-color', 'line-gap-width', 'line-offset', 'line-opacity', 'line-pattern', 'line-width']),
  circle: new Set([
    'circle-blur',
    'circle-color',
    'circle-opacity',
    'circle-radius',
    'circle-stroke-color',
    'circle-stroke-opacity',
    'circle-stroke-width',
  ]),
  symbol: new Set([
    'icon-color',
    'icon-halo-blur',
    'icon-halo-color',
    'icon-halo-width',
    'icon-opacity',
    'text-color',
    'text-halo-blur',
    'text-halo-color',
    'text-halo-width',
    'text-opacity',
  ]),
  'fill-extrusion': new Set([
    'fill-extrusion-base',
    'fill-extrusion-color',
    'fill-extrusion-height',
    'fill-extrusion-pattern',
  ]),
};

const allowedCustomLayoutPropsByKind: Record<LayerRuleStyleKind, Set<string>> = {
  fill: new Set(['fill-sort-key']),
  line: new Set(['line-join', 'line-sort-key']),
  circle: new Set(['circle-sort-key']),
  symbol: new Set([
    'symbol-sort-key',
    'icon-anchor',
    'icon-allow-overlap',
    'icon-image',
    'icon-ignore-placement',
    'icon-offset',
    'icon-overlap',
    'icon-rotate',
    'icon-size',
    'text-allow-overlap',
    'text-anchor',
    'text-field',
    'text-font',
    'text-ignore-placement',
    'text-justify',
    'text-letter-spacing',
    'text-max-width',
    'text-offset',
    'text-overlap',
    'text-radial-offset',
    'text-rotate',
    'text-size',
    'text-transform',
  ]),
  'fill-extrusion': new Set(),
};

const STATIC_SYMBOL_LAYOUT_PROPS = new Set<string>([
  'icon-allow-overlap',
  'icon-ignore-placement',
  'icon-overlap',
  'text-allow-overlap',
  'text-ignore-placement',
  'text-overlap',
]);

const ensureBaselineBagProp = (
  bag: PaintBaseline | LayoutBaseline,
  record: Record<string, unknown> | null | undefined,
  prop: string,
) => {
  if (Object.prototype.hasOwnProperty.call(bag.defined, prop)) {
    return;
  }
  bag.defined[prop] = hasOwn(record, prop);
  bag.values[prop] = record ? record[prop] : undefined;
};

const ensureBaseline = (layer: Record<string, unknown>, kind: LayerRuleStyleKind, meta: WorkbenchMeta): WorkbenchBaseline => {
  const existing = meta.baseline;
  if (existing && typeof existing === 'object') {
    if (!existing.layout || typeof existing.layout !== 'object') {
      existing.layout = { defined: {}, values: {} };
    }
    return existing;
  }

  const baseline: WorkbenchBaseline = {
    filter: (layer as any).filter ?? null,
    paint: { defined: {}, values: {} },
    layout: { defined: {}, values: {} },
  };

  const paint = isRecord((layer as any).paint) ? ((layer as any).paint as Record<string, unknown>) : null;
  const props = propNamesForKind(kind);
  [...props.color, ...props.opacity].forEach(prop => {
    ensureBaselineBagProp(baseline.paint, paint, prop);
  });

  meta.baseline = baseline;
  return baseline;
};

const restoreMaybeDeleteRecordProp = (
  record: Record<string, unknown>,
  baseline: PaintBaseline | LayoutBaseline,
  prop: string,
) => {
  if (baseline.defined[prop]) {
    record[prop] = baseline.values[prop];
  } else {
    delete record[prop];
  }
};

const restoreMaybeDeletePaintProp = (
  paint: Record<string, unknown>,
  baseline: WorkbenchBaseline,
  prop: string,
) => restoreMaybeDeleteRecordProp(paint, baseline.paint, prop);

const restoreMaybeDeleteLayoutProp = (
  layout: Record<string, unknown>,
  baseline: WorkbenchBaseline,
  prop: string,
) => restoreMaybeDeleteRecordProp(layout, baseline.layout, prop);

const applyFilter = (layerRecord: any, baseline: WorkbenchBaseline, union: Expr | null) => {
  if (!union) {
    if (baseline.filter === null) {
      delete layerRecord.filter;
    } else {
      layerRecord.filter = baseline.filter;
    }
    return;
  }

  if (Array.isArray(baseline.filter)) {
    layerRecord.filter = ['all', baseline.filter, union];
    return;
  }

  layerRecord.filter = union;
};

const defaultPaintFallback = (prop: string): unknown => {
  switch (prop) {
    case 'fill-antialias':
    case 'fill-extrusion-vertical-gradient':
      return true;
    case 'fill-opacity':
    case 'line-opacity':
    case 'circle-opacity':
    case 'text-opacity':
    case 'icon-opacity':
    case 'fill-extrusion-opacity':
      return 1;
    case 'line-gap-width':
    case 'line-offset':
    case 'line-blur':
    case 'circle-blur':
    case 'circle-stroke-opacity':
    case 'icon-halo-width':
    case 'icon-halo-blur':
    case 'text-halo-width':
    case 'text-halo-blur':
      return 0;
    case 'line-width':
      return 2;
    case 'circle-radius':
      return 5;
    case 'circle-stroke-width':
      return 1;
    case 'fill-translate':
    case 'line-translate':
    case 'circle-translate':
    case 'icon-translate':
    case 'text-translate':
    case 'fill-extrusion-translate':
      return [0, 0];
    case 'fill-outline-color':
      return '#000000';
    case 'circle-stroke-color':
      return '#ffffff';
    case 'icon-color':
    case 'text-color':
      return '#000000';
    case 'icon-halo-color':
    case 'text-halo-color':
      return 'transparent';
    case 'fill-translate-anchor':
    case 'line-translate-anchor':
    case 'circle-translate-anchor':
    case 'icon-translate-anchor':
    case 'text-translate-anchor':
    case 'fill-extrusion-translate-anchor':
      return 'map';
    case 'circle-pitch-scale':
      return 'map';
    case 'circle-pitch-alignment':
      return 'viewport';
    case 'fill-extrusion-height':
      return 10;
    case 'fill-extrusion-base':
      return 0;
    default:
      return undefined;
  }
};

const defaultLayoutFallback = (prop: string): unknown => {
  switch (prop) {
    case 'fill-sort-key':
    case 'circle-sort-key':
    case 'line-sort-key':
    case 'symbol-sort-key':
      return 0;
    case 'symbol-placement':
      return 'point';
    case 'symbol-spacing':
      return 250;
    case 'symbol-avoid-edges':
      return false;
    case 'symbol-z-order':
      return 'auto';
    case 'line-cap':
      return 'butt';
    case 'line-join':
      return 'miter';
    case 'line-miter-limit':
      return 2;
    case 'line-round-limit':
      return 1.05;
    case 'icon-image':
    case 'text-field':
      return '';
    case 'icon-size':
      return 1;
    case 'icon-allow-overlap':
    case 'icon-ignore-placement':
    case 'icon-optional':
      return false;
    case 'icon-overlap':
      return 'never';
    case 'icon-rotation-alignment':
    case 'icon-pitch-alignment':
    case 'text-pitch-alignment':
    case 'text-rotation-alignment':
      return 'auto';
    case 'icon-text-fit':
      return 'none';
    case 'icon-text-fit-padding':
      return [0, 0, 0, 0];
    case 'icon-rotate':
      return 0;
    case 'icon-padding':
    case 'text-padding':
      return 2;
    case 'icon-keep-upright':
      return false;
    case 'icon-offset':
    case 'text-offset':
      return [0, 0];
    case 'icon-anchor':
    case 'text-anchor':
      return 'center';
    case 'text-size':
      return 16;
    case 'text-font':
      return ['Open Sans Regular', 'Arial Unicode MS Regular'];
    case 'text-max-width':
      return 10;
    case 'text-line-height':
      return 1.2;
    case 'text-letter-spacing':
    case 'text-radial-offset':
    case 'text-rotate':
      return 0;
    case 'text-justify':
      return 'center';
    case 'text-max-angle':
      return 45;
    case 'text-writing-mode':
      return ['horizontal'];
    case 'text-keep-upright':
      return true;
    case 'text-transform':
      return 'none';
    case 'text-allow-overlap':
    case 'text-ignore-placement':
    case 'text-optional':
      return false;
    case 'text-overlap':
      return 'never';
    default:
      return undefined;
  }
};

const normalizeExpressionLiteralValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    if (value.length > 0 && typeof value[0] === 'string' && MAPLIBRE_EXPRESSION_OPERATORS.has(value[0])) {
      return value;
    }
    return ['literal', value];
  }
  if (isRecord(value)) {
    return ['literal', value];
  }
  return value;
};

const isZoomExpressionInput = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length === 1 &&
  value[0] === 'zoom';

const parseTopLevelZoomCurveExpression = (value: unknown): ZoomCurveExpression | null => {
  if (!Array.isArray(value) || value.length < 4 || typeof value[0] !== 'string') {
    return null;
  }

  if (
    (value[0] === 'interpolate' || value[0] === 'interpolate-hcl' || value[0] === 'interpolate-lab') &&
    isZoomExpressionInput(value[2])
  ) {
    const stops: Array<{ input: number; output: unknown }> = [];
    for (let index = 3; index + 1 < value.length; index += 2) {
      const input = value[index];
      if (typeof input !== 'number') {
        return null;
      }
      stops.push({ input, output: value[index + 1] });
    }
    if (stops.length === 0) {
      return null;
    }
    return {
      kind: 'interpolate',
      operator: value[0],
      interpolation: value[1],
      stops,
    };
  }

  if (value[0] === 'step' && isZoomExpressionInput(value[1])) {
    const defaultOutput = value[2];
    const stops: Array<{ input: number; output: unknown }> = [];
    for (let index = 3; index + 1 < value.length; index += 2) {
      const input = value[index];
      if (typeof input !== 'number') {
        return null;
      }
      stops.push({ input, output: value[index + 1] });
    }
    return {
      kind: 'step',
      defaultOutput,
      stops,
    };
  }

  return null;
};

const zoomCurveSignature = (curve: ZoomCurveExpression): string =>
  JSON.stringify({
    kind: curve.kind,
    operator: curve.kind === 'interpolate' ? curve.operator : 'step',
    interpolation: curve.kind === 'interpolate' ? curve.interpolation : null,
    stops: curve.stops.map(stop => stop.input),
  });

const buildRuleCaseExpression = (
  enabledRules: EnabledRule[],
  fieldsById: Map<string, MapComponentFilterField>,
  fallback: unknown,
  getRuleValue: (rule: EnabledRule) => unknown,
): Expr | null => {
  if (enabledRules.length === 0 || fallback === undefined) {
    return null;
  }

  const parts: any[] = ['case'];
  enabledRules.forEach(rule => {
    const predicate = buildPredicate(rule.logic as any, fieldsById);
    if (!predicate) {
      return;
    }
    const value = getRuleValue(rule);
    if (value === undefined) {
      return;
    }
    parts.push(predicate, normalizeExpressionLiteralValue(value));
  });
  parts.push(normalizeExpressionLiteralValue(fallback));
  return parts.length > 3 ? parts : null;
};

const buildRuleZoomCurveExpression = (
  enabledRules: EnabledRule[],
  fieldsById: Map<string, MapComponentFilterField>,
  fallback: unknown,
  getRuleValue: (rule: EnabledRule) => unknown,
): Expr | null => {
  const fallbackCurve = parseTopLevelZoomCurveExpression(fallback);
  const ruleCurves = enabledRules
    .map(rule => ({
      rule,
      value: getRuleValue(rule),
    }))
    .filter((entry): entry is { rule: EnabledRule; value: unknown } => entry.value !== undefined)
    .map(entry => ({
      rule: entry.rule,
      value: entry.value,
      curve: parseTopLevelZoomCurveExpression(entry.value),
    }));

  const templateCurve = fallbackCurve ?? ruleCurves.find(entry => entry.curve)?.curve ?? null;
  if (!templateCurve) {
    return null;
  }

  const templateSignature = zoomCurveSignature(templateCurve);
  if (
    fallbackCurve &&
    zoomCurveSignature(fallbackCurve) !== templateSignature
  ) {
    return null;
  }

  const hasIncompatibleRuleCurve = ruleCurves.some(
    entry => entry.curve && zoomCurveSignature(entry.curve) !== templateSignature,
  );
  if (hasIncompatibleRuleCurve) {
    return null;
  }

  const resolveOutputAtStop = (value: unknown, stopIndex: number, isDefaultStepOutput = false): unknown => {
    const curve = parseTopLevelZoomCurveExpression(value);
    if (!curve) {
      return value;
    }
    if (curve.kind === 'interpolate') {
      return curve.stops[stopIndex]?.output;
    }
    if (isDefaultStepOutput) {
      return curve.defaultOutput;
    }
    return curve.stops[stopIndex]?.output;
  };

  if (templateCurve.kind === 'interpolate') {
    const parts: any[] = [templateCurve.operator, templateCurve.interpolation, ['zoom']];
    templateCurve.stops.forEach((stop, stopIndex) => {
      const stopExpr = buildRuleCaseExpression(
        enabledRules,
        fieldsById,
        resolveOutputAtStop(fallback, stopIndex),
        rule => resolveOutputAtStop(getRuleValue(rule), stopIndex),
      );
      if (!stopExpr) {
        return;
      }
      parts.push(stop.input, stopExpr);
    });
    return parts.length > 5 ? parts : null;
  }

  const parts: any[] = [
    'step',
    ['zoom'],
    buildRuleCaseExpression(
      enabledRules,
      fieldsById,
      resolveOutputAtStop(fallback, 0, true),
      rule => resolveOutputAtStop(getRuleValue(rule), 0, true),
    ),
  ];
  templateCurve.stops.forEach((stop, stopIndex) => {
    const stopExpr = buildRuleCaseExpression(
      enabledRules,
      fieldsById,
      resolveOutputAtStop(fallback, stopIndex),
      rule => resolveOutputAtStop(getRuleValue(rule), stopIndex),
    );
    if (!stopExpr) {
      return;
    }
    parts.push(stop.input, stopExpr);
  });
  return parts.length > 4 ? parts : null;
};

const FIELD_TOKEN_PATTERN = /\{([a-zA-Z0-9_.-]+)\}/g;

const buildTextFieldTemplateExpression = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  const segments: Array<{ kind: 'text'; value: string } | { kind: 'field'; value: string }> = [];
  let cursor = 0;
  let match: RegExpExecArray | null = null;

  FIELD_TOKEN_PATTERN.lastIndex = 0;
  while ((match = FIELD_TOKEN_PATTERN.exec(value)) !== null) {
    const [token, fieldName] = match;
    if (match.index > cursor) {
      segments.push({ kind: 'text', value: value.slice(cursor, match.index) });
    }
    segments.push({ kind: 'field', value: fieldName });
    cursor = match.index + token.length;
  }

  if (cursor < value.length) {
    segments.push({ kind: 'text', value: value.slice(cursor) });
  }

  if (segments.length === 0 || !segments.some(segment => segment.kind === 'field')) {
    return value;
  }

  if (segments.length === 1 && segments[0]?.kind === 'field') {
    return ['get', segments[0].value];
  }

  return [
    'concat',
    ...segments.map(segment =>
      segment.kind === 'field'
        ? ['to-string', ['coalesce', ['get', segment.value], '']]
        : segment.value,
    ),
  ];
};

const buildRuleExpression = (
  layer: LayerRuleLayerState,
  fallback: unknown,
  getRuleValue: (rule: LayerRuleLayerState['rules'][number]) => unknown,
): Expr | null => {
  const enabled = layer.rules.filter(rule => rule.enabled);
  if (enabled.length === 0 || fallback === undefined) {
    return null;
  }

  const fieldsById = new Map<string, MapComponentFilterField>();
  layer.fields.forEach(field => fieldsById.set(field.id, field));

  return buildRuleCaseExpression(enabled, fieldsById, fallback, getRuleValue);
};

const buildRulePaintExpression = (
  layer: LayerRuleLayerState,
  prop: string,
  fallback: unknown,
  options?: { useRuleColorFallback?: boolean },
): Expr | null =>
  buildRuleExpression(layer, fallback, rule => {
    const paint = normalizeMapLibrePaintOverrides(isRecord(rule.paint) ? (rule.paint as Record<string, unknown>) : null);
    if (hasOwn(paint, prop)) {
      return paint?.[prop];
    }
    if (options?.useRuleColorFallback) {
      return normalizeAdvancedRuleColor(rule.color);
    }
    return undefined;
  });

const buildRuleLayoutExpression = (
  layer: LayerRuleLayerState,
  prop: string,
  fallback: unknown,
): Expr | null => {
  const normalizedFallback = prop === 'text-field' ? buildTextFieldTemplateExpression(fallback) : fallback;
  const getRuleValue = (rule: LayerRuleLayerState['rules'][number]) => {
    const layout = normalizeMapLibreLayoutOverrides(isRecord(rule.layout) ? (rule.layout as Record<string, unknown>) : null);
    if (!hasOwn(layout, prop)) {
      return undefined;
    }
    return prop === 'text-field' ? buildTextFieldTemplateExpression(layout?.[prop]) : layout?.[prop];
  };

  if (prop === 'text-size' || prop === 'icon-size') {
    const enabledRules = layer.rules.filter(rule => rule.enabled);
    const fieldsById = new Map<string, MapComponentFilterField>();
    layer.fields.forEach(field => fieldsById.set(field.id, field));
    const zoomExpr = buildRuleZoomCurveExpression(enabledRules, fieldsById, normalizedFallback, getRuleValue);
    if (zoomExpr) {
      return zoomExpr;
    }
  }

  return buildRuleExpression(layer, normalizedFallback, getRuleValue);
};

const resolveOverlapMode = (
  layout: Record<string, unknown> | null | undefined,
  overlapProp: 'icon-overlap' | 'text-overlap',
  allowProp: 'icon-allow-overlap' | 'text-allow-overlap',
) => {
  if (typeof layout?.[overlapProp] === 'string') {
    return layout[overlapProp];
  }
  if (typeof layout?.[allowProp] === 'boolean') {
    return layout[allowProp] ? 'always' : 'never';
  }
  return undefined;
};

const resolveStaticBooleanRuleLayoutValue = (
  layer: LayerRuleLayerState,
  prop: 'icon-ignore-placement' | 'text-ignore-placement',
  fallback: boolean,
) => {
  const enabledRules = layer.rules.filter(rule => rule.enabled);
  let sawExplicit = false;
  let sawTrue = false;
  let sawFalse = false;

  for (const rule of enabledRules) {
    const layout = normalizeMapLibreLayoutOverrides(isRecord(rule.layout) ? (rule.layout as Record<string, unknown>) : null);
    const value = layout?.[prop];
    if (typeof value !== 'boolean') {
      continue;
    }
    sawExplicit = true;
    if (value) {
      sawTrue = true;
    } else {
      sawFalse = true;
    }
  }

  if (sawTrue) {
    return true;
  }
  if (sawExplicit && sawFalse) {
    return false;
  }
  return fallback;
};

const resolveStaticOverlapRuleLayoutValue = (
  layer: LayerRuleLayerState,
  overlapProp: 'icon-overlap' | 'text-overlap',
  allowProp: 'icon-allow-overlap' | 'text-allow-overlap',
  fallback: 'never' | 'always' | 'cooperative',
) => {
  const enabledRules = layer.rules.filter(rule => rule.enabled);
  let sawExplicit = false;
  let sawAlways = false;
  let sawCooperative = false;
  let sawNever = false;

  for (const rule of enabledRules) {
    const layout = normalizeMapLibreLayoutOverrides(isRecord(rule.layout) ? (rule.layout as Record<string, unknown>) : null);
    const value = resolveOverlapMode(layout, overlapProp, allowProp);
    if (value !== 'never' && value !== 'always' && value !== 'cooperative') {
      continue;
    }
    sawExplicit = true;
    if (value === 'always') {
      sawAlways = true;
    } else if (value === 'cooperative') {
      sawCooperative = true;
    } else {
      sawNever = true;
    }
  }

  if (sawAlways) {
    return 'always';
  }
  if (sawCooperative) {
    return 'cooperative';
  }
  if (sawExplicit && sawNever) {
    return 'never';
  }
  return fallback;
};

const resolveSourceId = (
  source: Record<string, unknown> | undefined,
  workbench: WorkbenchMeta,
): string => {
  const sourceIdFromMeta = typeof workbench.source_id === 'string' ? workbench.source_id : '';
  if (sourceIdFromMeta) {
    return sourceIdFromMeta;
  }

  const url = typeof source?.url === 'string' ? source.url : '';
  if (url.startsWith('pmtiles://')) {
    return url.slice('pmtiles://'.length);
  }

  const data = typeof source?.data === 'string' ? source.data : '';
  if (data.startsWith('geojson://')) {
    return data.slice('geojson://'.length);
  }

  return data.match(MAP_SOURCE_GEOJSON_PATTERN)?.[1] ?? '';
};

export const compileStyleWithLayerRules = (
  styleJson: Record<string, unknown>,
  state: LayerRuleBuilderState,
): Record<string, unknown> => {
  const layers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  const sources = isRecord(styleJson.sources) ? (styleJson.sources as Record<string, any>) : {};
  const byKey = new Map<string, LayerRuleLayerState>();
  state.layers.forEach(layer => {
    const key = `${layer.sourceId}::${layer.sourceLayer}`;
    byKey.set(key, layer);
  });

  const nextLayers = layers.map(layer => {
    const metadata = isRecord((layer as any).metadata) ? ((layer as any).metadata as Record<string, unknown>) : {};
    const workbench = isRecord((metadata as any).workbench) ? ((metadata as any).workbench as WorkbenchMeta) : {};
    const sourceAlias =
      typeof (layer as any).source === 'string'
        ? ((layer as any).source as string)
        : typeof workbench.source_alias === 'string'
          ? workbench.source_alias
          : '';
    const sourceLayerFromLayer = typeof (layer as any)['source-layer'] === 'string' ? ((layer as any)['source-layer'] as string) : '';
    const sourceLayer =
      (typeof workbench.source_layer === 'string' ? workbench.source_layer : sourceLayerFromLayer) || '';
    const source = sourceAlias ? (sources[sourceAlias] as Record<string, unknown> | undefined) : undefined;
    const sourceId = resolveSourceId(source, workbench);
    if (!sourceId) {
      return layer;
    }
    if (!sourceLayer) {
      return layer;
    }

    const key = `${sourceId}::${sourceLayer}`;
    const config = byKey.get(key);
    if (!config) {
      return layer;
    }

    const layerType = typeof (layer as any).type === 'string' ? ((layer as any).type as string) : '';
    const kind: LayerRuleStyleKind =
      layerType === 'fill'
        ? 'fill'
        : layerType === 'line'
          ? 'line'
          : layerType === 'circle'
            ? 'circle'
            : layerType === 'fill-extrusion'
              ? 'fill-extrusion'
              : 'symbol';

    const nextLayer: Record<string, unknown> = { ...layer };
    const nextMetadata = isRecord((nextLayer as any).metadata) ? ((nextLayer as any).metadata as Record<string, unknown>) : {};
    const nextWorkbench = isRecord((nextMetadata as any).workbench) ? ((nextMetadata as any).workbench as WorkbenchMeta) : {};

    // "Opt in" any eligible layer once a rule-set exists for its (source_id, source_layer).
    nextWorkbench.managed = true;
    nextWorkbench.source_id = sourceId;
    nextWorkbench.source_alias = sourceAlias;
    nextWorkbench.source_layer = sourceLayer;
    nextWorkbench.kind = kind;

    nextMetadata.workbench = nextWorkbench as any;
    nextLayer.metadata = nextMetadata;

    const union = ruleUnionPredicate(config);
    const baseline = ensureBaseline(nextLayer, kind, nextWorkbench);

    const originalPaint = isRecord((nextLayer as any).paint) ? ((nextLayer as any).paint as Record<string, unknown>) : null;
    const originalLayout = isRecord((nextLayer as any).layout) ? ((nextLayer as any).layout as Record<string, unknown>) : null;
    const paint = originalPaint ? ({ ...originalPaint } as Record<string, unknown>) : {};
    const layout = originalLayout ? ({ ...originalLayout } as Record<string, unknown>) : {};
    const props = propNamesForKind(kind);
    const enabledRules = config.rules.filter(rule => rule.enabled);

    // Highlight colors always apply to matching features (even in hide mode).
    props.color.forEach(colorProp => {
      ensureBaselineBagProp(baseline.paint, originalPaint, colorProp);
      const baselineColor = baseline.paint.defined[colorProp] ? baseline.paint.values[colorProp] : defaultPaintFallback(colorProp);
      const expr = buildRulePaintExpression(config, colorProp, baselineColor, { useRuleColorFallback: true });
      if (expr) {
        paint[colorProp] = expr;
      } else {
        restoreMaybeDeletePaintProp(paint, baseline, colorProp);
      }
    });

    const customPaintProps = new Set<string>();
    const customLayoutProps = new Set<string>();
    enabledRules.forEach(rule => {
      if (isRecord(rule.paint)) {
        Object.keys(rule.paint).forEach(prop => customPaintProps.add(prop));
      }
      if (isRecord(rule.layout)) {
        Object.keys(rule.layout).forEach(prop => customLayoutProps.add(prop));
      }
    });

    customPaintProps.forEach(prop => {
      if (!allowedCustomPaintPropsByKind[kind].has(prop)) {
        return;
      }
      if (props.color.includes(prop) || props.opacity.includes(prop)) {
        return;
      }
      ensureBaselineBagProp(baseline.paint, originalPaint, prop);
      const fallback = baseline.paint.defined[prop] ? baseline.paint.values[prop] : defaultPaintFallback(prop);
      const expr = buildRulePaintExpression(config, prop, fallback);
      if (expr) {
        paint[prop] = expr;
      } else {
        restoreMaybeDeletePaintProp(paint, baseline, prop);
      }
    });

    customLayoutProps.forEach(prop => {
      if (!allowedCustomLayoutPropsByKind[kind].has(prop)) {
        return;
      }
      if (kind === 'symbol' && STATIC_SYMBOL_LAYOUT_PROPS.has(prop)) {
        return;
      }
      ensureBaselineBagProp(baseline.layout, originalLayout, prop);
      const fallback = baseline.layout.defined[prop] ? baseline.layout.values[prop] : defaultLayoutFallback(prop);
      const expr = buildRuleLayoutExpression(config, prop, fallback);
      if (expr) {
        layout[prop] = expr;
      } else {
        restoreMaybeDeleteLayoutProp(layout, baseline, prop);
      }
    });

    if (kind === 'symbol') {
      const applyOverlapMode = (
        overlapProp: 'icon-overlap' | 'text-overlap',
        allowProp: 'icon-allow-overlap' | 'text-allow-overlap',
      ) => {
        if (!customLayoutProps.has(overlapProp) && !customLayoutProps.has(allowProp)) {
          return;
        }
        ensureBaselineBagProp(baseline.layout, originalLayout, overlapProp);
        ensureBaselineBagProp(baseline.layout, originalLayout, allowProp);
        const fallback =
          resolveOverlapMode(
            {
              [overlapProp]: baseline.layout.values[overlapProp],
              [allowProp]: baseline.layout.values[allowProp],
            },
            overlapProp,
            allowProp,
          ) ?? defaultLayoutFallback(overlapProp);
        const resolvedMode = resolveStaticOverlapRuleLayoutValue(config, overlapProp, allowProp, fallback as 'never' | 'always' | 'cooperative');
        layout[overlapProp] = resolvedMode;
        layout[allowProp] = resolvedMode === 'always';
      };

      const applyIgnorePlacement = (
        prop: 'icon-ignore-placement' | 'text-ignore-placement',
      ) => {
        if (!customLayoutProps.has(prop)) {
          return;
        }
        ensureBaselineBagProp(baseline.layout, originalLayout, prop);
        const fallback =
          typeof baseline.layout.values[prop] === 'boolean'
            ? baseline.layout.values[prop]
            : Boolean(defaultLayoutFallback(prop));
        layout[prop] = resolveStaticBooleanRuleLayoutValue(config, prop, fallback);
      };

      applyOverlapMode('icon-overlap', 'icon-allow-overlap');
      applyOverlapMode('text-overlap', 'text-allow-overlap');
      applyIgnorePlacement('icon-ignore-placement');
      applyIgnorePlacement('text-ignore-placement');
    }

    applyFilter(nextLayer as any, baseline, union ?? RULES_HIDE_ALL_FILTER);
    props.opacity.forEach(opacityProp => {
      ensureBaselineBagProp(baseline.paint, originalPaint, opacityProp);
      const fallback = baseline.paint.defined[opacityProp] ? baseline.paint.values[opacityProp] : defaultPaintFallback(opacityProp);
      const expr = buildRulePaintExpression(config, opacityProp, fallback);
      if (expr) {
        paint[opacityProp] = expr;
      } else {
        restoreMaybeDeletePaintProp(paint, baseline, opacityProp);
      }
    });

    nextLayer.paint = paint;
    nextLayer.layout = layout;
    return nextLayer;
  });

  return {
    ...styleJson,
    layers: nextLayers,
  };
};
